import React, { useCallback, type ComponentType } from 'react';
import {
  PALETTE_DRAG_LABEL_MIME,
  PALETTE_DRAG_MIME,
  type PaletteType,
} from '../paletteDrop';
import {
  ItemBody,
  ItemIcon,
  ItemLabel,
  ItemRow,
} from './NodeLibrary.styled';

interface NodeLibraryItemProps {
  /**
   * The palette type to dispatch — `input` / `output` / `gate` / `note` for
   * structural nodes, `task:<skillId>` for skill-bound tasks.
   */
  paletteType: PaletteType;
  label: string;
  /**
   * Description / blurb for the node type. NOT rendered in the row itself
   * (the design is single-line label only — see `.ops-row__sub` in the
   * prototype, which is hidden in the flyout variant), but threaded
   * through to the row's `title` so it appears as a native browser
   * tooltip on hover for users who want the extra context.
   */
  description: string;
  icon: ComponentType<{ size?: number | string }>;
  /**
   * Called on click — caller usually drops the node at the canvas centre
   * (so keyboard-only users can add nodes without a drag gesture).
   */
  onSelect?: (paletteType: PaletteType) => void;
  dataTestId?: string;
}

/**
 * A single drag-source row in the NodeLibrary flyout. Sets the palette
 * MIME on `dataTransfer` so the canvas can resolve it back to a node
 * spec on drop. Also exposes a click handler so users without a drag
 * affordance (keyboard, touch) can still add nodes.
 *
 * Visual matches the prototype's `.ops-panel .ops-row`: a 30x30
 * lavender-tinted circular icon chip on the left, a single bold label
 * on the right, inside a pill-shaped surface-container-low row that
 * adds a 1px inset outline on hover.
 */
const NodeLibraryItem: React.FC<NodeLibraryItemProps> = ({
  paletteType,
  label,
  description,
  icon: Icon,
  onSelect,
  dataTestId,
}) => {
  const onDragStart = useCallback(
    (event: React.DragEvent<HTMLButtonElement>) => {
      event.dataTransfer.setData(PALETTE_DRAG_MIME, paletteType);
      // Carry the palette item's display label so the drop handler can
      // seed the new node's editable label with something meaningful (e.g.
      // "CAD convert") instead of the generic type-derived default.
      event.dataTransfer.setData(PALETTE_DRAG_LABEL_MIME, label);
      event.dataTransfer.effectAllowed = 'move';
    },
    [paletteType, label],
  );

  const onClick = useCallback(() => {
    onSelect?.(paletteType);
  }, [onSelect, paletteType]);

  return (
    <ItemRow
      type="button"
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={dataTestId ?? `node-library__item-${paletteType}`}
      data-palette-type={paletteType}
      aria-label={`Add ${label}`}
      title={description || label}
    >
      <ItemIcon aria-hidden="true">
        <Icon />
      </ItemIcon>
      <ItemBody>
        <ItemLabel>{label}</ItemLabel>
      </ItemBody>
    </ItemRow>
  );
};

export default NodeLibraryItem;
